// What hovering a trend chart points at, and what its tooltip says: kept
// apart from the chart so it can be read (and tested) without a DOM. Lines
// are { key, label, tone, dashed, of, points: [{ x, y, row }] } in the
// chart's viewBox units; `of` names the line a previous period belongs to.

// How far (in viewBox units, which are the plot's CSS pixels) the pointer
// may be from a line for that line to be the one hovered.
export const HOVER_RADIUS = 24

// The bucket under the pointer: the point whose x is nearest, the first on
// a tie.
export function nearestIndex(points, x) {
  let nearest = 0
  let distance = Infinity
  points.forEach((point, index) => {
    const delta = Math.abs(point.x - x)
    if (delta < distance) {
      distance = delta
      nearest = index
    }
  })
  return nearest
}

// The line under the pointer at that bucket: the one whose value there is
// nearest `y`, within `radius`, else none. Solid lines are tried first, so
// where a line crosses its own previous period the line wins.
export function nearestLine(lines, index, y, radius = HOVER_RADIUS) {
  const candidates = [...lines.filter((line) => !line.dashed), ...lines.filter((line) => line.dashed)]
  let nearest = null
  let distance = Infinity
  for (const line of candidates) {
    const point = line.points[index]
    if (point?.y == null) continue
    const delta = Math.abs(point.y - y)
    if (delta <= radius && delta < distance) {
      distance = delta
      nearest = line.key
    }
  }
  return nearest
}

// The line a focus key stands for: a previous period focuses its own line;
// a key naming no line focuses nothing (the rows may have changed under it).
export function focusLine(lines, key) {
  if (key == null) return null
  const line = lines.find((item) => item.key === key)
  if (!line) return null
  return line.of ?? line.key
}

// How a line (or its legend entry) is drawn while one is in focus: the line
// in focus and its previous period stand out, the rest step back.
export function lineState(item, focusKey) {
  if (focusKey == null) return null
  return item.key === focusKey || (item.of != null && item.of === focusKey) ? 'active' : 'muted'
}

// The tooltip's rows at a bucket: the lines, biggest value first (a missing
// value last), each with its previous period under it. A previous period
// whose line isn't drawn is listed at the end on its own; a line with no
// point at the bucket is left out.
export function tooltipRows(lines, index, focusKey) {
  const present = lines.filter((line) => line.points[index] !== undefined)
  const row = (line, paired) => ({
    key: line.key,
    label: line.label,
    value: line.points[index].row?.value ?? null,
    tone: line.tone,
    dashed: Boolean(line.dashed),
    paired,
    active: focusKey != null && line.key === focusKey,
  })
  const primaries = present.filter((line) => line.of == null)
  const primaryKeys = new Set(primaries.map((line) => line.key))
  const ordered = primaries
    .map((line, order) => ({ line, order, value: line.points[index].row?.value ?? null }))
    .sort((left, right) => {
      if (left.value == null || right.value == null) {
        if (left.value == null && right.value == null) return left.order - right.order
        return left.value == null ? 1 : -1
      }
      return right.value - left.value || left.order - right.order
    })
  const rows = []
  for (const { line } of ordered) {
    rows.push(row(line, false))
    for (const previous of present) {
      if (previous.of === line.key) rows.push(row(previous, true))
    }
  }
  for (const orphan of present) {
    if (orphan.of != null && !primaryKeys.has(orphan.of)) rows.push(row(orphan, false))
  }
  return rows
}

// Where a tooltip of `size` sits for a pointer (viewport pixels) inside
// `bounds`: below and to the right of the pointer, flipped to the left or
// above where it would run past the edge, then held inside the bounds (the
// top-left edge wins when it is bigger than they are).
export function tooltipPosition(pointer, size, bounds, { offset = 14, margin = 8 } = {}) {
  let left = pointer.x + offset
  let top = pointer.y + offset
  if (left + size.width > bounds.width - margin) left = pointer.x - offset - size.width
  if (top + size.height > bounds.height - margin) top = pointer.y - offset - size.height
  left = Math.max(margin, Math.min(left, bounds.width - size.width - margin))
  top = Math.max(margin, Math.min(top, bounds.height - size.height - margin))
  return { top, left }
}
